import { mapActions } from 'vuex'

import { cloneDeep, pick } from 'lodash'

import { Services, empty, fallbackConditionsValidator, isSendingsBulk, payloadValidator, recipientValidator } from '@sigma-frontend-libs/commons'

import { currentTab, generateServices, globalErrorHandler, request, sendingsOutputFilter } from '@/utils'

import { normalizePayload } from '../utils'
import render from './render'

export default {
  name: 'SendingsCreateTemplate',

  mixins: [
    generateServices([
      {
        name: Services.sendings,

        outputFilter: sendingsOutputFilter,

        find: false,
        get: false,
        update: false,
        remove: false,
        create: {
          async method(data, params = {}) {
            const response = await request.create(Services.sendings, { body: data }, params?.query)
            if (response.status === 'failed') {
              throw new Error(response.error)
            }

            return response
          },
          params: {
            query: {
              $scope: [ 'full' ]
            }
          }
        }
      }
    ]),

    currentTab('create')
  ],

  data() {
    return {
      showMessagePreview: false,

      template: undefined,
      isApplyingTemplate: false,
      appliedTemplateSnapshot: undefined,

      formValidation: false
    }
  },

  computed: {
    tabs() {
      return [
        {
          key: 'create',
          title: this.getTranslate(`${Services.sendings}.tabs.create`)
        },
        {
          key: Services.templates,
          title: this.getTranslate(`${Services.sendings}.tabs.templates`),
          callback: () => {
            this.template = undefined
          }
        },
        {
          key: 'preview',
          title: this.getTranslate('sendingsCreate.titles.preview')
        }
      ]
    },

    isBulk() {
      return isSendingsBulk(this.restData[Services.sendings]?.create?.data)
    },

    hasFallbacks() {
      return this.restData[Services.sendings].create.data?.fallbacks?.length > 0
    },

    recipientValidation() {
      return recipientValidator(this.restData[Services.sendings].create.data.recipient)
    },

    payloadValidation() {
      if (this.hasFallbacks) {
        return payloadValidator(this.restData[Services.sendings].create.data.payload, this.restData[Services.sendings].create.data.type)
      }

      return this.formValidation
    },

    fallbacksValidation() {
      if (this.hasFallbacks) {
        return this.restData[Services.sendings].create.data.fallbacks.every(fallback => {
          return payloadValidator(fallback.payload, fallback.type) && fallbackConditionsValidator(fallback.$options)
        })
      }

      return true
    },

    validation() {
      return this.recipientValidation && this.payloadValidation && this.fallbacksValidation
    }
  },

  watch: {
    isBulk(value) {
      if (!value) {
        this.restData[Services.sendings].create.data.$options.title = undefined
      }
    },

    [`restData.${Services.sendings}.create.data`]: {
      handler() {
        this.resetTemplateOnChange()
      },
      deep: true
    }
  },

  methods: {
    ...mapActions({ setCurrentTab: 'currentTab/setCurrentTab' }),

    async createSending() {
      try {
        const result = await this.rest[Services.sendings].create(this.restData[Services.sendings].create.data)
        if (result) {
          this.restData[Services.sendings].create.data = cloneDeep(empty.sendings.create)
        }
      } catch (error) {
        globalErrorHandler(error)
      }
    },

    reset() {
      this.restData[Services.sendings].create.data = cloneDeep(empty.sendings.create)
    },

    getTemplateComparisonSnapshot() {
      const data = this.restData[Services.sendings].create.data

      return JSON.stringify(pick(data, [ 'type', 'payload', 'recipient', 'fallbacks' ]))
    },

    resetTemplateOnChange() {
      if (!this.template || this.isApplyingTemplate) {
        return
      }

      if (this.getTemplateComparisonSnapshot() !== this.appliedTemplateSnapshot) {
        this.template = undefined
        this.appliedTemplateSnapshot = undefined
      }
    },

    setTemplate(value) {
      this.isApplyingTemplate = true

      const template = cloneDeep(value)
      const data = this.restData[Services.sendings].create.data

      data.type = template.type
      data.payload = normalizePayload(template.type, template.payload)

      if (template.recipient) {
        data.recipient = cloneDeep(template.recipient)
      }
      if (Array.isArray(template.fallbacks)) {
        data.fallbacks = cloneDeep(template.fallbacks)
      }

      this.template = template
      this.appliedTemplateSnapshot = this.getTemplateComparisonSnapshot()

      this.$nextTick(() => {
        this.isApplyingTemplate = false
        this.currentTab = 'create'
      })
    }
  },

  render
}
